/**
 * The attribute names `r-embed` reads.
 *
 * Four, and the shortest list that still produces a usable embed: what to
 * frame, what to call it, what shape it is, and whether it loads before the
 * visitor scrolls to it.
 *
 * There is no provider attribute. The provider is derived from the URL, so
 * pasting is the whole interaction and an author cannot pick YouTube and then
 * paste a Vimeo link. There is no width, colour, border or radius either —
 * those are column settings, and a second route to the same CSS property only
 * raises the question of which one is in force.
 *
 * Nothing here can influence the frame's privileges. `sandbox`, `allow` and
 * `referrerpolicy` are fixed per provider in `embed-providers.ts` and are not
 * attributes at all, which is also why this component deliberately has no
 * `pass-through-embed-attributes-*` convention.
 */
export const EMBED_URL_ATTRIBUTE = 'embed-url-3-resolutions';
export const EMBED_TITLE_ATTRIBUTE = 'embed-title-3-resolutions';
export const EMBED_ASPECT_RATIO_ATTRIBUTE = 'embed-aspect-ratio-3-resolutions';
export const EMBED_LOADING_ATTRIBUTE = 'embed-loading-3-resolutions';

/**
 * Shapes rather than dimensions. An iframe has no intrinsic size to preserve,
 * so something has to state the proportion or the box collapses to the
 * browser's 300×150 default — and a ratio does it without the author having to
 * work out a height for every screen width.
 *
 * `none` is the escape hatch for content that has no natural shape, a booking
 * form being the obvious one. It is the only value that hands sizing back to
 * the column's Height setting.
 */
export const EMBED_ASPECT_RATIOS = ['16:9', '4:3', '1:1', '9:16', '21:9', 'none'] as const;
export type EmbedAspectRatio = (typeof EMBED_ASPECT_RATIOS)[number];

/**
 * What an unshaped embed is tall by default. Applied by the component rather
 * than seeded onto the column, so the column's Height still overrides it and
 * clearing that returns the embed to a height that works — the same
 * "unset means the component's default" arrangement `r-button` uses for its
 * preset. Sized for a Calendly month view, which is the content that needs it.
 */
export const EMBED_UNSHAPED_HEIGHT = '700px';

export const EMBED_LOADING_VALUES = ['lazy', 'eager'] as const;
export type EmbedLoading = (typeof EMBED_LOADING_VALUES)[number];

/**
 * Lazy by default. An embed is a whole third-party document — for YouTube,
 * comfortably more than the rest of the page around it — and most of them sit
 * below the fold. `eager` exists for the ones that do not.
 */
export const DEFAULT_EMBED_LOADING: EmbedLoading = 'lazy';

type Attributes = { [key: string]: any } | undefined;

/**
 * The aspect ratio, or `undefined` when the author has not chosen one and the
 * provider's own default should apply. Anything unrecognised is treated as
 * unset rather than passed through: this value reaches a CSS property.
 */
export const resolveEmbedAspectRatio = (
  componentAttributes: Attributes
): EmbedAspectRatio | undefined => {
  const value = componentAttributes?.[EMBED_ASPECT_RATIO_ATTRIBUTE];
  return EMBED_ASPECT_RATIOS.includes(value) ? value : undefined;
};

export const resolveEmbedLoading = (componentAttributes: Attributes): EmbedLoading => {
  const value = componentAttributes?.[EMBED_LOADING_ATTRIBUTE];
  return value === 'eager' ? 'eager' : DEFAULT_EMBED_LOADING;
};

/**
 * An iframe's accessible name. A screen reader reaching an untitled frame
 * announces it as "frame" and nothing else, so this never returns empty — an
 * author who clears the field gets the provider's name back rather than a
 * silent frame.
 */
export const resolveEmbedTitle = (
  componentAttributes: Attributes,
  fallback: string
): string => {
  const value = componentAttributes?.[EMBED_TITLE_ATTRIBUTE];
  if (typeof value !== 'string') {
    return fallback;
  }
  const trimmed = value.trim();
  return trimmed === '' ? fallback : trimmed;
};

/** CSS `aspect-ratio` wants `16 / 9`; the attribute stores the familiar `16:9`. */
export const toCssAspectRatio = (ratio: EmbedAspectRatio): string | undefined =>
  ratio === 'none' ? undefined : ratio.replace(':', ' / ');
